import {
  evaluateCenteringCurve,
  hasCenteringCurveMidpoint,
  isValidCenteringCurve,
  sampleCenteringCurve,
} from './advancedCenteringCurve'
import type {
  BorderLine as CenteringProjectionLine,
  CenteringProjection,
  CenteringProjectionGeometry,
  CenteringProjectionSection,
  ImageRectification,
} from '../../lib/types'

export type RectificationPoint = { x: number; y: number }
export type RectificationBox = { x1: number; y1: number; x2: number; y2: number }

const CENTERING_CURVE_SAMPLES = 24
const SVG_CURVE_SAMPLES = 16
const HOMOGRAPHY_EPSILON = 1e-10

export function getAppliedImageRectification(
  projection: CenteringProjection | null | undefined,
  expectedSourceShape?: readonly [number, number] | null,
): ImageRectification | null {
  const rectification = projection?.image_rectification
  if (
    projection?.coordinate_space !== 'source_pixels' ||
    !rectification ||
    rectification.version !== 1 ||
    rectification.status !== 'applied' ||
    rectification.source_coordinate_space !== 'source_pixels' ||
    rectification.display_coordinate_space !== 'rectified_pixels' ||
    !isImageShape(rectification.source_shape) ||
    !isImageShape(rectification.display_shape) ||
    !isHomography(rectification.homography) ||
    (rectification.inverse_homography !== undefined &&
      !isHomography(rectification.inverse_homography)) ||
    (rectification.source_quad !== undefined && !isPointTupleList(rectification.source_quad))
  ) {
    return null
  }

  const sourceShape = expectedSourceShape ?? projection.image_shape
  if (
    !isImageShape(sourceShape) ||
    sourceShape[0] !== rectification.source_shape[0] ||
    sourceShape[1] !== rectification.source_shape[1]
  ) {
    return null
  }
  return rectification
}

export function applyImageHomography(
  point: RectificationPoint,
  rectification: Pick<ImageRectification, 'homography'>,
): RectificationPoint | null {
  const matrix = rectification.homography
  const denominator = matrix[2][0] * point.x + matrix[2][1] * point.y + matrix[2][2]
  if (!Number.isFinite(denominator) || Math.abs(denominator) <= HOMOGRAPHY_EPSILON) return null
  const x = (matrix[0][0] * point.x + matrix[0][1] * point.y + matrix[0][2]) / denominator
  const y = (matrix[1][0] * point.x + matrix[1][1] * point.y + matrix[1][2]) / denominator
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null
}

export function transformImageBoxForRectifiedDisplay(
  box: RectificationBox,
  rectification: Pick<ImageRectification, 'homography'>,
): RectificationBox | null {
  const corners = [
    { x: box.x1, y: box.y1 },
    { x: box.x2, y: box.y1 },
    { x: box.x2, y: box.y2 },
    { x: box.x1, y: box.y2 },
  ].map((point) => applyImageHomography(point, rectification))
  if (corners.some((point) => !point)) return null

  const transformed = corners as RectificationPoint[]
  const xs = transformed.map((point) => point.x)
  const ys = transformed.map((point) => point.y)
  return {
    x1: Math.min(...xs),
    y1: Math.min(...ys),
    x2: Math.max(...xs),
    y2: Math.max(...ys),
  }
}

/**
 * Re-expresses a source-to-rectified homography for coordinates from another
 * raster of the same source image. Damage masks are stored at inference
 * resolution while centering projections retain the full source resolution.
 */
export function scaleImageRectificationSourceShape(
  rectification: ImageRectification,
  sourceShape: readonly [number, number],
): ImageRectification | null {
  if (
    !isImageShape(rectification.source_shape) ||
    !isImageShape(rectification.display_shape) ||
    !isImageShape(sourceShape) ||
    !isHomography(rectification.homography) ||
    (rectification.inverse_homography !== undefined &&
      !isHomography(rectification.inverse_homography)) ||
    (rectification.source_quad !== undefined && !isPointTupleList(rectification.source_quad))
  ) {
    return null
  }

  const [sourceHeight, sourceWidth] = sourceShape
  const scaleX = (rectification.source_shape[1] - 1) / (sourceWidth - 1)
  const scaleY = (rectification.source_shape[0] - 1) / (sourceHeight - 1)
  if (!Number.isFinite(scaleX) || !Number.isFinite(scaleY) || scaleX <= 0 || scaleY <= 0) {
    return null
  }

  const sourceScale: ImageRectification['homography'] = [
    [scaleX, 0, 0],
    [0, scaleY, 0],
    [0, 0, 1],
  ]
  const inverseSourceScale: ImageRectification['homography'] = [
    [1 / scaleX, 0, 0],
    [0, 1 / scaleY, 0],
    [0, 0, 1],
  ]

  return {
    ...rectification,
    source_shape: [sourceHeight, sourceWidth],
    homography: multiplyHomographies(rectification.homography, sourceScale),
    inverse_homography: rectification.inverse_homography
      ? multiplyHomographies(inverseSourceScale, rectification.inverse_homography)
      : undefined,
    source_quad: rectification.source_quad?.map(([x, y]) => [x / scaleX, y / scaleY]),
  }
}

export function transformCenteringProjectionForDisplay(
  projection: CenteringProjection,
  geometry: CenteringProjectionGeometry | null | undefined,
  rectification: ImageRectification,
): {
  projection: CenteringProjection
  geometry: CenteringProjectionGeometry | null
} | null {
  if (
    projection.coordinate_space !== 'source_pixels' ||
    !Array.isArray(projection.image_shape) ||
    projection.image_shape.length !== 2 ||
    projection.image_shape[0] !== rectification.source_shape[0] ||
    projection.image_shape[1] !== rectification.source_shape[1] ||
    !projection.sides ||
    !projection.axes ||
    !(['left', 'right', 'top', 'bottom'] as const).every(
      (side) =>
        Boolean(projection.sides[side]) &&
        typeof projection.sides[side] === 'object' &&
        !Array.isArray(projection.sides[side]),
    ) ||
    !(['horizontal', 'vertical'] as const).every(
      (axis) =>
        Boolean(projection.axes[axis]) &&
        typeof projection.axes[axis] === 'object' &&
        !Array.isArray(projection.axes[axis]) &&
        Array.isArray(projection.axes[axis].sections),
    )
  ) {
    return null
  }

  const transformOptionalLine = (line: CenteringProjectionLine | null | undefined) =>
    line ? transformCenteringLine(line, rectification) : line
  const sides = { ...projection.sides }
  for (const side of ['left', 'right', 'top', 'bottom'] as const) {
    const projectedSide = projection.sides[side]
    const outerLine = transformOptionalLine(projectedSide.outer_line)
    const innerLine = transformOptionalLine(projectedSide.inner_line)
    if ((projectedSide.outer_line && !outerLine) || (projectedSide.inner_line && !innerLine)) {
      return null
    }
    sides[side] = {
      ...projectedSide,
      outer_line: outerLine,
      inner_line: innerLine,
    }
  }

  const transformSection = (
    section: CenteringProjectionSection | null | undefined,
  ): CenteringProjectionSection | null => {
    if (!section || typeof section !== 'object' || Array.isArray(section)) return null
    const firstSegment = transformOptionalLine(section.first_segment)
    const secondSegment = transformOptionalLine(section.second_segment)
    if ((section.first_segment && !firstSegment) || (section.second_segment && !secondSegment)) {
      return null
    }
    return {
      ...section,
      first_segment: firstSegment ?? undefined,
      second_segment: secondSegment ?? undefined,
    }
  }
  const axes = { ...projection.axes }
  for (const axisName of ['horizontal', 'vertical'] as const) {
    const sections = projection.axes[axisName].sections.map(transformSection)
    if (sections.some((section) => !section)) return null
    axes[axisName] = {
      ...projection.axes[axisName],
      sections: sections as CenteringProjectionSection[],
    }
  }

  const anchorMeasurement = projection.rectified_anchor_measurement
  if (
    anchorMeasurement != null &&
    (typeof anchorMeasurement !== 'object' || Array.isArray(anchorMeasurement))
  ) {
    return null
  }
  const outerFrame = anchorMeasurement?.outer_frame
  if (outerFrame != null && (typeof outerFrame !== 'object' || Array.isArray(outerFrame))) {
    return null
  }
  const sourceQuad = outerFrame?.source_quad
  if (sourceQuad != null && !isPointTupleList(sourceQuad)) return null
  const transformedSourceQuad = sourceQuad
    ? transformPointTupleList(sourceQuad, rectification)
    : sourceQuad
  if (sourceQuad && !transformedSourceQuad) return null

  const transformedGeometry = geometry ? transformCenteringGeometry(geometry, rectification) : null
  if (geometry && !transformedGeometry) return null

  return {
    projection: {
      ...projection,
      coordinate_space: 'rectified_pixels',
      image_shape: [...rectification.display_shape],
      sides,
      axes,
      rectified_anchor_measurement: anchorMeasurement
        ? {
            ...anchorMeasurement,
            outer_frame: outerFrame
              ? {
                  ...outerFrame,
                  source_quad: transformedSourceQuad,
                }
              : outerFrame,
          }
        : projection.rectified_anchor_measurement,
    },
    geometry: transformedGeometry,
  }
}

export function transformSvgPathsForRectifiedDisplay(
  sourcePaths: readonly string[] | null | undefined,
  rectification: ImageRectification,
): { paths: string[]; sourcePaths: string[] } {
  const transformed: { paths: string[]; sourcePaths: string[] } = {
    paths: [],
    sourcePaths: [],
  }
  for (const sourcePath of sourcePaths ?? []) {
    const path = transformSvgPathForRectifiedDisplay(sourcePath, rectification)
    if (!path) continue
    transformed.paths.push(path)
    transformed.sourcePaths.push(sourcePath)
  }
  return transformed
}

export function transformSvgPathForRectifiedDisplay(
  sourcePath: string,
  rectification: ImageRectification,
): string | null {
  const tokens =
    sourcePath.match(/[AaCcHhLlMmQqSsTtVvZz]|[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/g) ?? []
  if (tokens.length === 0) return null

  const output: string[] = []
  let tokenIndex = 0
  let command = ''
  let previousCommand = ''
  let current = { x: 0, y: 0 }
  let subpathStart = { x: 0, y: 0 }
  let lastCubicControl: RectificationPoint | null = null
  let lastQuadraticControl: RectificationPoint | null = null

  const appendPoint = (kind: 'M' | 'L', point: RectificationPoint) => {
    const transformed = applyImageHomography(point, rectification)
    if (!transformed) return false
    output.push(`${kind} ${formatCoordinate(transformed.x)} ${formatCoordinate(transformed.y)}`)
    return true
  }
  const appendCurve = (evaluate: (parameter: number) => RectificationPoint, samples: number) => {
    for (let sample = 1; sample <= samples; sample += 1) {
      if (!appendPoint('L', evaluate(sample / samples))) return false
    }
    return true
  }

  while (tokenIndex < tokens.length) {
    if (isSvgCommand(tokens[tokenIndex])) {
      command = tokens[tokenIndex]
      tokenIndex += 1
    } else if (!command) {
      return null
    }

    const upper = command.toUpperCase()
    const relative = command !== upper
    if (upper === 'Z') {
      output.push('Z')
      current = { ...subpathStart }
      lastCubicControl = null
      lastQuadraticControl = null
      previousCommand = upper
      command = ''
      continue
    }

    const parameterCount = SVG_PARAMETER_COUNTS[upper]
    if (!parameterCount || tokenIndex + parameterCount > tokens.length) return null
    if (isSvgCommand(tokens[tokenIndex])) return null
    const values = tokens.slice(tokenIndex, tokenIndex + parameterCount).map(Number)
    if (!values.every(Number.isFinite)) return null
    tokenIndex += parameterCount
    const currentX = current.x
    const currentY = current.y
    const absolutePoint = (x: number, y: number) =>
      relative ? { x: currentX + x, y: currentY + y } : { x, y }

    if (upper === 'M') {
      const point = absolutePoint(values[0], values[1])
      if (!appendPoint(output.length === 0 ? 'M' : 'M', point)) return null
      current = point
      subpathStart = point
      command = relative ? 'l' : 'L'
    } else if (upper === 'L') {
      const point = absolutePoint(values[0], values[1])
      if (!appendPoint('L', point)) return null
      current = point
    } else if (upper === 'H') {
      const point = { x: relative ? current.x + values[0] : values[0], y: current.y }
      if (!appendPoint('L', point)) return null
      current = point
    } else if (upper === 'V') {
      const point = { x: current.x, y: relative ? current.y + values[0] : values[0] }
      if (!appendPoint('L', point)) return null
      current = point
    } else if (upper === 'C' || upper === 'S') {
      const firstControl =
        upper === 'C'
          ? absolutePoint(values[0], values[1])
          : previousCommand === 'C' || previousCommand === 'S'
            ? reflectPoint(lastCubicControl ?? current, current)
            : { ...current }
      const secondControl = absolutePoint(
        values[upper === 'C' ? 2 : 0],
        values[upper === 'C' ? 3 : 1],
      )
      const end = absolutePoint(values[upper === 'C' ? 4 : 2], values[upper === 'C' ? 5 : 3])
      const start = current
      if (
        !appendCurve(
          (parameter) => evaluateCubic(start, firstControl, secondControl, end, parameter),
          SVG_CURVE_SAMPLES,
        )
      ) {
        return null
      }
      current = end
      lastCubicControl = secondControl
      lastQuadraticControl = null
    } else if (upper === 'Q' || upper === 'T') {
      const control: RectificationPoint =
        upper === 'Q'
          ? absolutePoint(values[0], values[1])
          : previousCommand === 'Q' || previousCommand === 'T'
            ? reflectPoint(lastQuadraticControl ?? current, current)
            : { ...current }
      const end = absolutePoint(values[upper === 'Q' ? 2 : 0], values[upper === 'Q' ? 3 : 1])
      const start = current
      if (
        !appendCurve(
          (parameter) => evaluateQuadratic(start, control, end, parameter),
          SVG_CURVE_SAMPLES,
        )
      ) {
        return null
      }
      current = end
      lastQuadraticControl = control
      lastCubicControl = null
    } else if (upper === 'A') {
      const end = absolutePoint(values[5], values[6])
      const arc = buildArcSampler(
        current,
        end,
        values[0],
        values[1],
        values[2],
        values[3] !== 0,
        values[4] !== 0,
      )
      if (arc) {
        if (!appendCurve(arc.evaluate, arc.samples)) return null
      } else if (!appendPoint('L', end)) {
        return null
      }
      current = end
      lastCubicControl = null
      lastQuadraticControl = null
    } else {
      return null
    }

    if (upper !== 'C' && upper !== 'S') lastCubicControl = null
    if (upper !== 'Q' && upper !== 'T') lastQuadraticControl = null
    previousCommand = upper
  }

  return output.length > 0 ? output.join(' ') : null
}

function transformCenteringGeometry(
  geometry: CenteringProjectionGeometry,
  rectification: ImageRectification,
): CenteringProjectionGeometry | null {
  if (
    geometry.coordinate_space !== 'source_pixels' ||
    !Array.isArray(geometry.image_shape) ||
    geometry.image_shape.length !== 2 ||
    geometry.image_shape[0] !== rectification.source_shape[0] ||
    geometry.image_shape[1] !== rectification.source_shape[1] ||
    !geometry.sides ||
    !(['left', 'right', 'top', 'bottom'] as const).every(
      (side) =>
        Boolean(geometry.sides[side]) &&
        typeof geometry.sides[side] === 'object' &&
        !Array.isArray(geometry.sides[side]),
    )
  ) {
    return null
  }
  const sides = { ...geometry.sides }
  for (const side of ['left', 'right', 'top', 'bottom'] as const) {
    const outerLine = transformCenteringLine(geometry.sides[side].outer_line, rectification)
    const innerLine = transformCenteringLine(geometry.sides[side].inner_line, rectification)
    if (!outerLine || !innerLine) return null
    sides[side] = { outer_line: outerLine, inner_line: innerLine }
  }
  return {
    ...geometry,
    coordinate_space: 'rectified_pixels',
    image_shape: [...rectification.display_shape],
    sides,
  }
}

function transformCenteringLine(
  line: CenteringProjectionLine,
  rectification: ImageRectification,
): CenteringProjectionLine | null {
  if (!isValidCenteringCurve(line)) return null
  const curved = hasCenteringCurveMidpoint(line)
  const sourcePoints = curved
    ? sampleCenteringCurve(line, CENTERING_CURVE_SAMPLES)
    : [
        { x: line.x1, y: line.y1 },
        { x: line.x2, y: line.y2 },
      ]
  const points = sourcePoints.map((point) => applyImageHomography(point, rectification))
  if (points.some((point) => !point)) return null
  const transformed = points as RectificationPoint[]
  const first = transformed[0]
  const last = transformed[transformed.length - 1]
  if (!curved) return { x1: first.x, y1: first.y, x2: last.x, y2: last.y }
  const middle = applyImageHomography(evaluateCenteringCurve(line, 0.5), rectification)
  if (!middle) return null
  return {
    x1: first.x,
    y1: first.y,
    xm: middle.x,
    ym: middle.y,
    x2: last.x,
    y2: last.y,
    ...(line.curve_kind ? { curve_kind: line.curve_kind } : {}),
    sampled_points: transformed.map((point) => [point.x, point.y]),
  }
}

function transformPointTupleList(
  points: unknown,
  rectification: ImageRectification,
): [number, number][] | null {
  if (!isPointTupleList(points)) return null
  const transformed = points.map(([x, y]) => applyImageHomography({ x, y }, rectification))
  if (transformed.some((point) => !point)) return null
  return (transformed as RectificationPoint[]).map((point) => [point.x, point.y])
}

function evaluateQuadratic(
  start: RectificationPoint,
  control: RectificationPoint,
  end: RectificationPoint,
  parameter: number,
) {
  const remaining = 1 - parameter
  return {
    x:
      remaining * remaining * start.x +
      2 * remaining * parameter * control.x +
      parameter * parameter * end.x,
    y:
      remaining * remaining * start.y +
      2 * remaining * parameter * control.y +
      parameter * parameter * end.y,
  }
}

function evaluateCubic(
  start: RectificationPoint,
  firstControl: RectificationPoint,
  secondControl: RectificationPoint,
  end: RectificationPoint,
  parameter: number,
) {
  const remaining = 1 - parameter
  return {
    x:
      remaining ** 3 * start.x +
      3 * remaining * remaining * parameter * firstControl.x +
      3 * remaining * parameter * parameter * secondControl.x +
      parameter ** 3 * end.x,
    y:
      remaining ** 3 * start.y +
      3 * remaining * remaining * parameter * firstControl.y +
      3 * remaining * parameter * parameter * secondControl.y +
      parameter ** 3 * end.y,
  }
}

function buildArcSampler(
  start: RectificationPoint,
  end: RectificationPoint,
  rawRadiusX: number,
  rawRadiusY: number,
  rotationDegrees: number,
  largeArc: boolean,
  sweep: boolean,
): { evaluate: (parameter: number) => RectificationPoint; samples: number } | null {
  let radiusX = Math.abs(rawRadiusX)
  let radiusY = Math.abs(rawRadiusY)
  if (
    radiusX <= Number.EPSILON ||
    radiusY <= Number.EPSILON ||
    (Math.abs(start.x - end.x) <= Number.EPSILON && Math.abs(start.y - end.y) <= Number.EPSILON)
  ) {
    return null
  }

  const rotation = (rotationDegrees * Math.PI) / 180
  const cosine = Math.cos(rotation)
  const sine = Math.sin(rotation)
  const deltaX = (start.x - end.x) / 2
  const deltaY = (start.y - end.y) / 2
  const localX = cosine * deltaX + sine * deltaY
  const localY = -sine * deltaX + cosine * deltaY
  const radiiScale =
    (localX * localX) / (radiusX * radiusX) + (localY * localY) / (radiusY * radiusY)
  if (radiiScale > 1) {
    const scale = Math.sqrt(radiiScale)
    radiusX *= scale
    radiusY *= scale
  }

  const radiusXSquared = radiusX * radiusX
  const radiusYSquared = radiusY * radiusY
  const numerator = Math.max(
    0,
    radiusXSquared * radiusYSquared -
      radiusXSquared * localY * localY -
      radiusYSquared * localX * localX,
  )
  const denominator = radiusXSquared * localY * localY + radiusYSquared * localX * localX
  const centerScale =
    (largeArc === sweep ? -1 : 1) *
    (denominator > Number.EPSILON ? Math.sqrt(numerator / denominator) : 0)
  const localCenterX = (centerScale * radiusX * localY) / radiusY
  const localCenterY = (-centerScale * radiusY * localX) / radiusX
  const center = {
    x: cosine * localCenterX - sine * localCenterY + (start.x + end.x) / 2,
    y: sine * localCenterX + cosine * localCenterY + (start.y + end.y) / 2,
  }
  const startVector = {
    x: (localX - localCenterX) / radiusX,
    y: (localY - localCenterY) / radiusY,
  }
  const endVector = {
    x: (-localX - localCenterX) / radiusX,
    y: (-localY - localCenterY) / radiusY,
  }
  const startAngle = Math.atan2(startVector.y, startVector.x)
  let angleDelta =
    Math.atan2(
      startVector.x * endVector.y - startVector.y * endVector.x,
      startVector.x * endVector.x + startVector.y * endVector.y,
    ) || 0
  if (!sweep && angleDelta > 0) angleDelta -= Math.PI * 2
  if (sweep && angleDelta < 0) angleDelta += Math.PI * 2

  return {
    samples: Math.max(4, Math.ceil(Math.abs(angleDelta) / (Math.PI / 12))),
    evaluate: (parameter) => {
      const angle = startAngle + angleDelta * parameter
      const localPointX = radiusX * Math.cos(angle)
      const localPointY = radiusY * Math.sin(angle)
      return {
        x: cosine * localPointX - sine * localPointY + center.x,
        y: sine * localPointX + cosine * localPointY + center.y,
      }
    },
  }
}

function reflectPoint(control: RectificationPoint, around: RectificationPoint) {
  return { x: 2 * around.x - control.x, y: 2 * around.y - control.y }
}

function formatCoordinate(value: number) {
  const rounded = Math.round(value * 1_000_000) / 1_000_000
  return Object.is(rounded, -0) ? '0' : String(rounded)
}

function isSvgCommand(value: string) {
  return /^[AaCcHhLlMmQqSsTtVvZz]$/.test(value)
}

function isImageShape(value: unknown): value is [number, number] {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    value.every(
      (dimension) => typeof dimension === 'number' && Number.isFinite(dimension) && dimension > 1,
    )
  )
}

function isHomography(value: unknown): value is ImageRectification['homography'] {
  return (
    Array.isArray(value) &&
    value.length === 3 &&
    value.every(
      (row) =>
        Array.isArray(row) &&
        row.length === 3 &&
        row.every((entry) => typeof entry === 'number' && Number.isFinite(entry)),
    )
  )
}

function isPointTupleList(value: unknown): value is [number, number][] {
  return (
    Array.isArray(value) &&
    value.length === 4 &&
    value.every(
      (point) =>
        Array.isArray(point) &&
        point.length === 2 &&
        point.every((coordinate) => typeof coordinate === 'number' && Number.isFinite(coordinate)),
    )
  )
}

function multiplyHomographies(
  left: ImageRectification['homography'],
  right: ImageRectification['homography'],
): ImageRectification['homography'] {
  return [0, 1, 2].map((row) =>
    [0, 1, 2].map(
      (column) =>
        left[row][0] * right[0][column] +
        left[row][1] * right[1][column] +
        left[row][2] * right[2][column],
    ),
  ) as ImageRectification['homography']
}

const SVG_PARAMETER_COUNTS: Record<string, number> = {
  M: 2,
  L: 2,
  H: 1,
  V: 1,
  C: 6,
  S: 4,
  Q: 4,
  T: 2,
  A: 7,
}
